<!--
  TrialBalancePaginated — one server page of trial balance plus PaginationFilter.
  First load sends page fc_trialbalance so the server applies the saved view.
  Later pages send those filters and sort explicitly.
  Excel download is a server file for the filtered set, not this page and not a browser workbook.
-->
<template>
  <div class="page-table-layout" data-testid="financialclose-project-trialbalance-paginated">
    <v-card
      class="all-width flex-card shadow-off"
      loader-height="2"
      :loading="loading || tbPage.loading"
      data-testid="financialclose-project-trialbalance-paginated__main-card"
    >
      <v-card-text class="page-table-layout__body">
        <TrialBalancePaginatedHeader
          v-if="!showSkeleton"
          :loading="loading || tbPage.loading || syncing"
          :upload-in-progress="uploadInProgress"
          :erp-enabled="!!$store.state.erpEnabled.tbErpEnabled"
          :is-manager="isManager"
          :last-updated="(lastRunData[0] || {}).lastupdated || ''"
          :exporting="exporting"
          :export-disabled="exportDisabled"
          :export-tooltip="exportTooltip"
          :show-closing-difference="showClosingDifference"
          :closing-balance-total="closingBalanceTotal"
          :metadata-id="metadata._id || ''"
          :activity-log-type="$options.ACTIVITY_LOG_TYPES.TRIAL_BALANCE"
          @refresh="refreshData"
          @sync="syncERP"
          @bulk-upload="openBulkUpload"
          @export="downloadExcel"
        />

        <div v-if="showSkeleton" data-testid="financialclose-project-trialbalance-paginated__loading">
          <v-skeleton-loader type="table-heading, list-item-two-line, article, article" />
        </div>

        <template v-else>
          <div class="trial-balance-paginated__toolbar">
            <div
              v-if="metadata._id && !metadata.tbInitialized"
              class="d-flex align-center trial-balance-paginated__warning"
              data-testid="financialclose-project-trialbalance-paginated__tb-warning"
            >
              <v-icon color="warning" small class="mr-1">mdi-alert</v-icon>
              <span>Trial balance (TB) not yet uploaded; hence values are showing zero in the TB</span>
            </div>
            <PaginationFilter
              ref="paginationFilter"
              :fields="filterFields"
              :saved-filters="savedFilters"
              :saved-sort="savedSort"
              :query-filters="activeFilters"
              :query-sort="activeSort"
              :total-count="tbPage.totalCount"
              :page="tbPage.page"
              :page-count="tbPage.totalPages"
              :page-size="pageSize"
              :loading="tbPage.loading"
              test-id-prefix="financialclose-project-trialbalance-paginated"
              @apply="onFilterApply"
              @save="onFilterSave"
              @page-change="onPageChange"
            >
              <template #after-sort>
                <v-tooltip bottom content-class="tooltip-bottom" :disabled="!saveOrderTooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <span class="trial-balance-paginated__save-order-wrap" v-bind="attrs" v-on="on">
                      <button
                        type="button"
                        class="trial-balance-paginated__save-order"
                        :class="{ 'trial-balance-paginated__save-order--highlighted': headersDirty }"
                        :disabled="saveOrderDisabled"
                        data-testid="financialclose-project-trialbalance-paginated__save-order-btn"
                        @click="saveHeaderOrder"
                      >
                        <v-icon>mdi-bookmark</v-icon>
                        <span>Save Order</span>
                      </button>
                    </span>
                  </template>
                  <span>{{ saveOrderTooltip }}</span>
                </v-tooltip>
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="trial-balance-paginated__clear-filter"
                  data-testid="financialclose-project-trialbalance-paginated__clear-filter-btn"
                  @click="clearFilters"
                >
                  Clear filter
                </button>
              </template>
              <template #before-pagination>
                <span class="trial-balance-paginated__toolbar-divider" aria-hidden="true" />
              </template>
              <template #toolbar-right>
                <CustomizeColumnMenu
                  v-model="customizeMenuOpen"
                  :headers="headers"
                  color="caccounting"
                  :show-save-button="false"
                  :show-apply-button="true"
                  :exclude-columns="['action', 'select', 'indexno']"
                  data-testid-prefix="financialclose-project-trialbalance-paginated"
                  @apply="handleApplyColumns"
                />
              </template>
            </PaginationFilter>
          </div>

          <div class="flex-fill-table">
            <div class="trial-balance-paginated__grid">
              <div
                v-if="tbPage.loading && !tbPage.records.length"
                data-testid="financialclose-project-trialbalance-paginated__table-loading"
              >
                <v-skeleton-loader type="table-tbody" />
              </div>
              <div
                v-else-if="!tbPage.loading && tbPage.totalCount === 0"
                class="trial-balance-paginated__empty"
                data-testid="financialclose-project-trialbalance-paginated__empty-state"
              >
                {{ $options.TB_PAGINATED_MESSAGES.EMPTY }}
              </div>
              <div v-else class="trial-balance-paginated__scroll">
                <table class="trial-balance-paginated__table" data-testid="financialclose-project-trialbalance-paginated__table">
                  <thead class="tableHeaderBg">
                    <tr>
                      <th
                        v-for="(header, headerIndex) in visibleHeaders"
                        :key="header.value"
                        class="tableHeaderBg trial-balance-paginated__header-cell"
                        :class="{
                          'trial-balance-paginated__cell--number': header.datatype === 'number',
                          'trial-balance-paginated__header-cell--number': header.datatype === 'number',
                          'trial-balance-paginated__header-cell--draggable': header.value !== 'indexno',
                          'trial-balance-paginated__header-cell--dragging': draggedHeaderKey === header.value,
                          'trial-balance-paginated__header-cell--drop-target': dropTargetKey === header.value,
                        }"
                        :draggable="header.value !== 'indexno'"
                        :data-testid="`financialclose-project-trialbalance-paginated__header--${header.value}`"
                        @dragstart="onHeaderDragStart(header, $event)"
                        @dragover="onHeaderDragOver(header, $event)"
                        @dragleave="onHeaderDragLeave(header, $event)"
                        @drop="onHeaderDrop(header, $event)"
                        @dragend="onHeaderDragEnd"
                      >
                        <div class="trial-balance-paginated__header-content">
                          <button
                            v-if="header.value !== 'indexno'"
                            type="button"
                            class="trial-balance-paginated__move-col trial-balance-paginated__move-col--left"
                            draggable="false"
                            :disabled="headerIndex === 0 || visibleHeaders[headerIndex - 1].value === 'indexno'"
                            :aria-label="`Move ${header.text} left`"
                            :data-testid="`financialclose-project-trialbalance-paginated__move-left--${header.value}`"
                            @mousedown.stop
                            @click.stop="moveColumn(header, -1)"
                          >
                            <v-icon small>mdi-chevron-left</v-icon>
                          </button>
                          <img
                            v-if="header.value !== 'indexno'"
                            src="/img/icons/fbc-icons/drag-drop.svg"
                            alt=""
                            draggable="false"
                            class="trial-balance-paginated__drag-icon"
                            :data-testid="`financialclose-project-trialbalance-paginated__drag-icon--${header.value}`"
                          />
                          <span>{{ header.text }}</span>
                          <button
                            v-if="header.value !== 'indexno'"
                            type="button"
                            class="trial-balance-paginated__move-col trial-balance-paginated__move-col--right"
                            draggable="false"
                            :disabled="headerIndex === visibleHeaders.length - 1"
                            :aria-label="`Move ${header.text} right`"
                            :data-testid="`financialclose-project-trialbalance-paginated__move-right--${header.value}`"
                            @mousedown.stop
                            @click.stop="moveColumn(header, 1)"
                          >
                            <v-icon small>mdi-chevron-right</v-icon>
                          </button>
                        </div>
                      </th>
                      <!-- Action stays last and is not a drag source or drop target. -->
                      <th
                        class="tableHeaderBg trial-balance-paginated__header-cell trial-balance-paginated__action-cell"
                        data-testid="financialclose-project-trialbalance-paginated__header--action"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in tbPage.records"
                      :key="item._id || index"
                      :data-testid="`financialclose-project-trialbalance-paginated__row--${index}`"
                    >
                      <td
                        v-for="header in visibleHeaders"
                        :key="header.value"
                        class="px-4"
                        :class="{ 'trial-balance-paginated__cell--number': header.datatype === 'number' }"
                      >
                        <div v-if="header.value === 'location'" class="d-flex align-center">
                          <span
                            v-if="locationNames(item).length"
                            class="d-inline-flex width-fit pa-2 caccounting-background radius-mini caccounting--text mr-2"
                          >
                            {{ locationNames(item)[0] }}
                          </span>
                          <v-tooltip v-if="locationNames(item).length > 1" top content-class="tooltip-top">
                            <template v-slot:activator="{ on }">
                              <span
                                v-on="on"
                                class="d-inline-flex width-fit radius-mini caccounting-background font-size-xm caccounting--text pa-2"
                              >
                                +{{ locationNames(item).length - 1 }}
                              </span>
                            </template>
                            <div v-for="(name, locIndex) in locationNames(item)" :key="locIndex">
                              <span>{{ name }}</span>
                            </div>
                          </v-tooltip>
                        </div>
                        <div v-else-if="header.value === 'reference_detail'" class="d-flex align-center">
                          <button
                            v-if="references(item)[0] && references(item)[0].ref"
                            type="button"
                            class="trial-balance-paginated__task-link d-inline-flex width-fit pa-2 caccounting-background radius-mini caccounting--text mr-2"
                            :data-testid="`financialclose-project-trialbalance-paginated__task-link--${index}`"
                            @click="openTaskDetail(references(item))"
                          >
                            {{ references(item)[0].ref }}
                          </button>
                          <v-tooltip v-if="references(item).length > 1" top content-class="tooltip-top">
                            <template v-slot:activator="{ on }">
                              <span
                                v-on="on"
                                class="d-inline-flex width-fit radius-mini caccounting-background font-size-xm caccounting--text pa-2 cursor-pointer"
                              >
                                +{{ references(item).length - 1 }}
                              </span>
                            </template>
                            <div v-for="(refItem, refIndex) in references(item).slice(1)" :key="refIndex">
                              <span>{{ refItem.ref }}</span>
                            </div>
                          </v-tooltip>
                        </div>
                        <span v-else-if="header.value === 'created_at'">{{ $nova.formatDate(item.created_at) }}</span>
                        <span v-else-if="header.value === 'updated_at'">{{ $nova.formatDate(item.updated_at) }}</span>
                        <span v-else-if="header.value === 'indexno'">{{ rowNumber(index) }}</span>
                        <span v-else-if="header.datatype === 'number'">{{ $nova.formatNumber(item[header.value]) }}</span>
                        <span v-else>{{ item[header.value] }}</span>
                      </td>
                      <td class="px-3 trial-balance-paginated__action-cell">
                        <div class="trial-balance-paginated__actions">
                          <v-tooltip bottom content-class="tooltip-bottom">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                v-bind="attrs"
                                v-on="on"
                                :disabled="uploadInProgress"
                                :data-testid="`financialclose-project-trialbalance-paginated__action-edit-btn--${index}`"
                                @click="openEdit(item)"
                              >
                                <v-icon>mdi-pencil-outline</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit</span>
                          </v-tooltip>
                          <activity-log
                            icon="/img/icons/activity-log.svg"
                            size="xs"
                            :resource-id="item._id"
                            :row-description="item.name"
                            :data-testid="`financialclose-project-trialbalance-paginated__row-activitylog-btn--${index}`"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </v-card-text>
    </v-card>

    <BulkUpload
      ref="bulkUploadComponent"
      :upload-type="$options.BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE"
      test-id-prefix="trial-balance-paginated"
      :project="id"
      :hideButton="true"
      @reload="refreshData"
    />

    <TaskDetailDrawer
      v-model="selectedTaskDetails"
      :loading="taskDetailLoading"
      :task="viewTaskDetails"
      :linked-tasks="listTask"
      :user-name-list="userNameList"
      :userid="userid"
      @select-task="viewTaskDetail"
    />

    <TrialBalanceEditDrawer
      v-model="editOpen"
      :item="editItem"
      :project-id="id"
      :location-names="editLocationNames"
      :location-label="locationLabel"
      :mutations-disabled="uploadInProgress"
      @saved="refreshData"
    />
  </div>
</template>

<script>
import './TrialBalancePaginated.scss';
import { mapActions, mapGetters } from 'vuex';
import {
  ActivityLog,
  BulkUpload,
  CustomizeColumnMenu,
  PaginationFilter,
} from '../../../components';
import {
  ACTIVITY_LOG_TYPES,
  BULK_CHECKLIST_UPLOAD_TYPE,
  TB_PAGINATED,
  TB_PAGINATED_MESSAGES,
  TB_UPLOAD_MESSAGES,
} from '../../../utils/constants';
import { tbUploadResultMessage } from '../../../utils/tbUpload';
import { countValidConditions } from '../../../components/PaginationFilter/PaginationFilterUtils';
import TaskDetailDrawer from '../components/TaskDetailDrawer.vue';
import TrialBalanceEditDrawer from './components/TrialBalanceEditDrawer.vue';
import TrialBalancePaginatedHeader from './components/TrialBalancePaginatedHeader.vue';
import {
  applySavedHeaderKeys,
  buildColumnHeaders,
  buildTbFilterFields,
  cloneViewFilters,
  cloneViewSort,
  columnsForExport,
  emptyViewFilters,
  headerKeysForSave,
  nextActiveFilters,
  nextActiveSort,
  locationNamesForRecord,
  referenceList,
  reorderHeaders,
  withPinnedIndexColumn,
} from './utils/TrialBalancePaginatedUtils';

export default {
  name: 'financialclose_project_trialbalance_paginated',

  ACTIVITY_LOG_TYPES,
  BULK_CHECKLIST_UPLOAD_TYPE,
  TB_PAGINATED_MESSAGES,

  components: {
    ActivityLog,
    BulkUpload,
    CustomizeColumnMenu,
    PaginationFilter,
    TaskDetailDrawer,
    TrialBalanceEditDrawer,
    TrialBalancePaginatedHeader,
  },

  data() {
    return {
      id: '',
      userid: '',
      loadedCycleId: '',
      initializing: true,
      skipNextActivate: false,
      headers: buildColumnHeaders('Location'),
      savedHeaderKeys: headerKeysForSave(buildColumnHeaders('Location')),
      savingHeaders: false,
      savedFilters: emptyViewFilters(),
      savedSort: [],
      activeFilters: emptyViewFilters(),
      activeSort: [],
      customizeMenuOpen: false,
      draggedHeaderKey: null,
      dropTargetKey: null,
      exporting: false,
      editOpen: false,
      editItem: {},
      editLocationNames: [],
      selectedTaskDetails: false,
      viewTaskDetails: null,
      taskDetailLoading: false,
      taskDetailRequest: 0,
      listTask: [],
      isDestroyed: false,
      bootstrapGeneration: 0,
      criteriaRequest: 0,
      syncing: false,
      sawTbUploadInProgress: false,
    };
  },

  computed: {
    ...mapGetters('trialBalance', [
      'metadata',
      'userNameList',
      'lastRunData',
      'loading',
      'locationNameById',
      'tbPage',
      'tbUpload',
    ]),
    pageSize() {
      return TB_PAGINATED.PAGE_SIZE;
    },
    locationLabel() {
      return this.$store.state.LocationHeader || 'Location';
    },
    filterFields() {
      return buildTbFilterFields(this.locationLabel);
    },
    visibleHeaders() {
      return this.headers.filter((header) => header.visible && header.value !== 'action');
    },
    headersDirty() {
      return headerKeysForSave(this.headers).join('\u0000') !== (this.savedHeaderKeys || []).join('\u0000');
    },
    listLoading() {
      return this.loading || this.tbPage.loading;
    },
    exportDisabled() {
      return this.exporting || this.listLoading || !this.tbPage.totalCount;
    },
    exportTooltip() {
      if (!this.exportDisabled) return '';
      if (this.exporting) return TB_PAGINATED_MESSAGES.EXPORT_IN_PROGRESS;
      if (this.listLoading) return TB_PAGINATED_MESSAGES.LIST_STILL_LOADING;
      return TB_PAGINATED_MESSAGES.NO_RECORDS_TO_EXPORT;
    },
    saveOrderDisabled() {
      return !this.headersDirty || this.savingHeaders || this.listLoading;
    },
    saveOrderTooltip() {
      if (!this.saveOrderDisabled) return '';
      if (this.listLoading) return TB_PAGINATED_MESSAGES.LIST_STILL_LOADING;
      if (!this.headersDirty) return TB_PAGINATED_MESSAGES.NO_COLUMN_CHANGES;
      return '';
    },
    hasActiveFilters() {
      return countValidConditions(this.activeFilters, this.filterFields) > 0;
    },
    exportColumns() {
      return columnsForExport(this.headers, this.locationLabel);
    },
    isManager() {
      const managers = this.metadata && this.metadata.managerlist;
      return Array.isArray(managers) && managers.indexOf(this.userid) > -1;
    },
    closingBalanceTotal() {
      const raw = this.metadata && this.metadata.totalClosingBalance;
      const num = typeof raw === 'number' ? raw : Number(String(raw == null ? '' : raw).replace(/,/g, ''));
      return Number.isFinite(num) ? num : 0;
    },
    showClosingDifference() {
      return Math.round(this.closingBalanceTotal) !== 0;
    },
    showSkeleton() {
      return !this.metadata._id && (this.initializing || this.loading);
    },
    uploadInProgress() {
      return !!(this.tbUpload && this.tbUpload.inProgress);
    },
  },

  created() {
    this.applyLocationHeader();
    this.skipNextActivate = true;
    this.bootstrap();
  },

  activated() {
    if (this.skipNextActivate) {
      this.skipNextActivate = false;
      return;
    }
    this.refreshData();
  },

  beforeDestroy() {
    this.isDestroyed = true;
    this.bootstrapGeneration += 1;
    this.taskDetailRequest += 1;
    this.cancelErpStatusReadAction();
    this.savedFilters = emptyViewFilters();
    this.savedSort = [];
    this.activeFilters = emptyViewFilters();
    this.activeSort = [];
    this.editItem = {};
    this.editLocationNames = [];
    this.editOpen = false;
    this.listTask = [];
    this.viewTaskDetails = null;
    this.exporting = false;
    this.savingHeaders = false;
    this.savedHeaderKeys = [];
    this.initializing = false;
    this.customizeMenuOpen = false;
    this.draggedHeaderKey = null;
    this.dropTargetKey = null;
    this.headers = [];
    this.sawTbUploadInProgress = false;
    this.stopTbUploadPollingAction();
    this.advanceTbUploadEpochAction();
  },

  watch: {
    '$route.params.id'(id) {
      if (!id || String(id) === String(this.loadedCycleId)) return;
      this.sawTbUploadInProgress = false;
      this.stopTbUploadPollingAction();
      this.advanceTbUploadEpochAction();
      this.bootstrap();
    },
    uploadInProgress(inProgress, wasInProgress) {
      if (inProgress) {
        this.sawTbUploadInProgress = true;
        this.editOpen = false;
        this.startTbUploadPollingAction({ id: this.id });
        return;
      }
      this.stopTbUploadPollingAction();
      if (!wasInProgress || !this.sawTbUploadInProgress) return;
      this.sawTbUploadInProgress = false;
      this.notifyTbUploadFinished();
      this.refreshData();
    },
    selectedTaskDetails(open) {
      if (!open) this.taskDetailRequest += 1;
    },
  },

  methods: {
    ...mapActions('trialBalance', {
      fetchTbMetaAction: 'fetchTbMeta',
      fetchTbPageAction: 'fetchTbPage',
      saveTbViewConfigAction: 'saveTbViewConfig',
      exportTrialBalanceAction: 'exportTrialBalance',
      fetchLastUpdatedAtAction: 'fetchLastUpdatedAt',
      cancelErpStatusReadAction: 'cancelErpStatusRead',
      syncERPAction: 'syncERP',
      fetchTaskAction: 'fetchTask',
      startTbUploadPollingAction: 'startTbUploadPolling',
      stopTbUploadPollingAction: 'stopTbUploadPolling',
      advanceTbUploadEpochAction: 'advanceTbUploadEpoch',
    }),

    applyLocationHeader() {
      const locationHeader = this.headers.find((header) => header.value === 'location');
      if (locationHeader) locationHeader.text = this.locationLabel;
    },

    locationNames(record) {
      return locationNamesForRecord(record, this.locationNameById);
    },

    references(record) {
      return referenceList(record);
    },

    rowNumber(index) {
      const page = Number(this.tbPage.page) || 1;
      const limit = Number(this.tbPage.limit) || this.pageSize;
      return ((page - 1) * limit) + index + 1;
    },

    /**
     * First open of a cycle asks the server for the saved view.
     * Refresh and later pages send the filters already applied.
     */
    bootstrap() {
      this.fetchMetaAndPage({ useSavedView: true, page: 1 });
    },

    refreshData() {
      this.fetchMetaAndPage({ useSavedView: false, page: 1 });
    },

    async fetchMetaAndPage({ useSavedView, page }) {
      const cycleId = this.$route.params.id || '';
      const generation = ++this.bootstrapGeneration;
      this.id = cycleId;
      this.userid = this.$store.state.user.id;
      const reset = String(this.loadedCycleId) !== String(cycleId);
      this.loadedCycleId = cycleId;
      if (reset) {
        this.headers = buildColumnHeaders(this.locationLabel);
        this.savedHeaderKeys = headerKeysForSave(this.headers);
      }
      const stillCurrent = () => !this.isDestroyed && generation === this.bootstrapGeneration;
      try {
        await Promise.all([
          this.fetchTbMetaAction({ id: cycleId, reset }),
          this.loadPage(page, useSavedView, reset),
        ]);
        if (!stillCurrent()) return;
        this.syncTbUploadPolling();
        await this.fetchLastUpdatedAtAction({
          id: cycleId,
          startDate: this.$nova.formatDate(this.metadata && this.metadata.from),
          endDate: this.$nova.formatDate(this.metadata && this.metadata.to),
        });
      } finally {
        if (stillCurrent()) this.initializing = false;
      }
    },

    /**
     * filters and sort are the criteria for this request only.
     * activeFilters and activeSort change after the page returns, so a failed
     * request leaves the rows and the export criteria on the previous view.
     */
    async loadPage(page, useSavedView, reset, filters, sort, filterType) {
      const requestFilters = filters === undefined ? this.activeFilters : filters;
      const requestSort = sort === undefined ? this.activeSort : sort;
      const data = await this.fetchTbPageAction({
        id: this.id,
        page,
        filters: useSavedView ? null : requestFilters,
        sort: useSavedView ? null : requestSort,
        useSavedView,
        reset,
        filterType: useSavedView ? undefined : filterType,
      });
      if (!data || this.isDestroyed) return false;
      this.activeFilters = nextActiveFilters({
        applied: data.appliedFilters,
        defaults: data.defaultFilters,
        requested: useSavedView ? null : requestFilters,
        useSavedView,
      });
      this.activeSort = nextActiveSort({
        applied: data.appliedSort,
        defaults: data.defaultSort,
        requested: useSavedView ? null : requestSort,
        useSavedView,
      });
      if (useSavedView) {
        const filter = this.$refs.paginationFilter;
        if (filter && typeof filter.allowSavedSync === 'function') filter.allowSavedSync();
        this.savedFilters = cloneViewFilters(data.defaultFilters);
        this.savedSort = cloneViewSort(data.defaultSort);
        if (Array.isArray(data.defaultHeaders) && data.defaultHeaders.length) {
          this.headers = applySavedHeaderKeys(this.headers, data.defaultHeaders, this.locationLabel);
          this.savedHeaderKeys = headerKeysForSave(this.headers);
        }
      }
      console.log('[TrialBalancePaginated]:', { page: data.page, totalCount: data.totalCount });
      return true;
    },

    async onFilterApply({ filters, sort, source }) {
      const token = ++this.criteriaRequest;
      const loaded = await this.loadPage(1, false, false, filters, sort, source);
      if (token !== this.criteriaRequest) return;
      const filter = this.$refs.paginationFilter;
      if (!filter) return;
      if (loaded && typeof filter.commitApplied === 'function') {
        filter.commitApplied(this.tbPage.appliedFilters, this.tbPage.appliedSort);
      }
      else if (typeof filter.rollbackCriteria === 'function') filter.rollbackCriteria();
    },

    /** Clears filters only. Sort stays. The toolbar updates after page 1 returns. */
    async clearFilters() {
      const token = ++this.criteriaRequest;
      const loaded = await this.loadPage(1, false, false, emptyViewFilters(), this.activeSort);
      if (token !== this.criteriaRequest || !loaded) return;
      const filter = this.$refs.paginationFilter;
      if (filter && typeof filter.clearAppliedFilters === 'function') filter.clearAppliedFilters();
    },

    /**
     * Saved Apply also emits apply, which refetches. This call only persists.
     * savedFilters and savedSort update after the save succeeds, so a failed save
     * does not look like the new preference.
     */
    async onFilterSave(payload) {
      const source = payload || {};
      const saved = await this.saveTbViewConfigAction(source);
      if (saved) {
        if (Object.prototype.hasOwnProperty.call(source, 'filters')) {
          this.savedFilters = cloneViewFilters(source.filters);
        }
        if (Object.prototype.hasOwnProperty.call(source, 'sort')) {
          this.savedSort = cloneViewSort(source.sort);
        }
        return;
      }
      const filter = this.$refs.paginationFilter;
      if (filter && typeof filter.restoreSavedDraft === 'function') filter.restoreSavedDraft();
    },

    async saveHeaderOrder() {
      if (this.saveOrderDisabled) return;
      const headers = headerKeysForSave(this.headers);
      this.savingHeaders = true;
      try {
        const saved = await this.saveTbViewConfigAction({ headers });
        if (saved) this.savedHeaderKeys = headers.slice();
      } finally {
        this.savingHeaders = false;
      }
    },

    onPageChange(page) {
      this.loadPage(page, false, false);
    },

    /**
     * Restart the status poll when this view loads onto a job already running.
     * The toast flag is set only after this tab has seen inProgress.
     */
    syncTbUploadPolling() {
      if (!this.id || !this.uploadInProgress) return;
      this.sawTbUploadInProgress = true;
      this.startTbUploadPollingAction({ id: this.id });
    },

    notifyTbUploadFinished() {
      const result = tbUploadResultMessage(this.tbUpload);
      if (result.error) {
        this.$store.commit('sbError', result.message || TB_UPLOAD_MESSAGES.FAILED);
        return;
      }
      this.$store.commit('sbSuccess', result.message || TB_UPLOAD_MESSAGES.COMPLETED);
      console.log('[TrialBalancePaginated]:', { tbUpload: 'completed' });
    },

    async syncERP() {
      if (this.syncing || this.uploadInProgress) return;
      this.syncing = true;
      try {
        await this.syncERPAction({
          id: this.id,
          startDate: this.metadata && this.metadata.from,
          endDate: this.metadata && this.metadata.to,
        });
      } finally {
        this.syncing = false;
      }
    },

    /**
     * Server workbook for every row matching the active filters and sort, not this page.
     */
    async downloadExcel() {
      if (this.exporting || this.exportDisabled) return;
      this.exporting = true;
      try {
        await this.exportTrialBalanceAction({
          id: this.id,
          filters: this.activeFilters,
          sort: this.activeSort,
          columns: this.exportColumns,
        });
      } finally {
        this.exporting = false;
      }
    },

    handleApplyColumns(headers) {
      this.headers = withPinnedIndexColumn(headers);
      this.customizeMenuOpen = false;
    },

    /**
     * Native HTML5 drag of the whole header cell.
     * The action column is a separate cell and is not draggable, so it stays last.
     */
    onHeaderDragStart(header, event) {
      if (!header || header.value === 'action' || header.value === 'indexno') {
        event.preventDefault();
        return;
      }
      this.draggedHeaderKey = header.value;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', header.value);
    },

    /** Drop is allowed on data columns only. Action is excluded so it cannot take a new index. */
    onHeaderDragOver(header, event) {
      if (!this.draggedHeaderKey || !header || header.value === 'action' || header.value === 'indexno') return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      if (header.value !== this.draggedHeaderKey) this.dropTargetKey = header.value;
    },

    onHeaderDragLeave(header, event) {
      if (!header || this.dropTargetKey !== header.value) return;
      const related = event.relatedTarget;
      if (!event.currentTarget.contains(related)) this.dropTargetKey = null;
    },

    /**
     * Reorder headers in place. Does not save; Save Order is the persist path.
     * Action is excluded so a drop cannot pull it off the end.
     */
    onHeaderDrop(header, event) {
      event.preventDefault();
      const sourceKey = this.draggedHeaderKey;
      const targetKey = header && header.value;
      this.clearHeaderDrag();
      if (!sourceKey || !targetKey || sourceKey === 'action' || targetKey === 'action') return;
      if (sourceKey === 'indexno' || targetKey === 'indexno') return;
      this.headers = reorderHeaders(this.headers, sourceKey, targetKey);
    },

    onHeaderDragEnd() {
      this.clearHeaderDrag();
    },

    /** Keyboard reorder. direction -1 moves before the previous visible column, 1 moves after the next. */
    moveColumn(header, direction) {
      if (!header || (direction !== -1 && direction !== 1)) return;
      if (header.value === 'indexno' || header.value === 'action') return;
      const visible = this.visibleHeaders;
      const index = visible.findIndex((item) => item.value === header.value);
      const neighbor = visible[index + direction];
      if (!neighbor || neighbor.value === 'indexno' || neighbor.value === 'action') return;
      const list = this.headers.slice();
      const from = list.findIndex((item) => item && item.value === header.value);
      if (from < 0) return;
      const [moved] = list.splice(from, 1);
      const target = list.findIndex((item) => item && item.value === neighbor.value);
      if (target < 0) return;
      list.splice(direction > 0 ? target + 1 : target, 0, moved);
      this.headers = withPinnedIndexColumn(list);
    },

    clearHeaderDrag() {
      this.draggedHeaderKey = null;
      this.dropTargetKey = null;
    },

    openEdit(item) {
      if (this.uploadInProgress) return;
      this.editItem = item || {};
      this.editLocationNames = this.locationNames(item);
      this.editOpen = true;
    },

    openBulkUpload() {
      if (this.uploadInProgress) return;
      if (this.$refs.bulkUploadComponent) {
        this.$refs.bulkUploadComponent.openBulkUpload();
      }
    },

    openTaskDetail(tasks) {
      this.listTask = Array.isArray(tasks) ? tasks : [];
      this.viewTaskDetail(this.listTask[0]);
      this.selectedTaskDetails = true;
    },

    async viewTaskDetail(task) {
      if (!task || !task._id) return;
      const requestId = ++this.taskDetailRequest;
      this.taskDetailLoading = true;
      this.viewTaskDetails = null;
      try {
        const enrichedTask = await this.fetchTaskAction({
          projectId: this.id,
          taskId: task._id,
        });
        if (requestId !== this.taskDetailRequest) return;
        const computed = enrichedTask.__computed || {};
        this.viewTaskDetails = {
          ...enrichedTask,
          __tbtotals: computed.tbtotals || { total: 0, items: [] },
          threshold: computed.threshold ?? 'NA',
          locationName: computed.locationName || '',
        };
      } catch (err) {
        if (requestId !== this.taskDetailRequest) return;
        console.error('[TrialBalancePaginated]:', err);
        this.$store.commit('sbError', (err && err.message) || 'Failed to fetch task details');
        this.viewTaskDetails = null;
      } finally {
        if (requestId === this.taskDetailRequest) this.taskDetailLoading = false;
      }
    },
  },
};
</script>
